import { AppLink } from '../components/AppLink';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Faq, faqLd } from '../components/Faq';
import { Icon } from '../components/Icon';
import { NotFoundContent } from '../components/NotFoundContent';
import { Picture } from '../components/Picture';
import { ConsultationBanner } from '../components/services/ConsultationBanner';
import { ProcessSection } from '../components/services/ProcessSection';
import { getProject, type Project } from '../data/projects';
import { getService, services } from '../data/services';
import { Seo, breadcrumbLd } from '../lib/seo';
import { SITE, absoluteUrl } from '../lib/site';
import { btnOutline, btnPrimary, container, eyebrow, h2Section } from '../lib/ui';
import type { PageProps } from '../routes';
import { images } from '../generated/images';

export default function ServiceDetail({ params }: PageProps) {
  const service = getService(params.slug ?? '');
  if (!service) return <NotFoundContent />;

  const s = service;
  const path = `/services/${s.slug}`;
  const trail: Array<[string, string]> = [
    ['Services', '/services'],
    [s.name, path],
  ];
  const related = s.detail.relatedProjects.map(getProject).filter((p): p is Project => Boolean(p));
  const others = services.filter((o) => o.slug !== s.slug);

  const img = images[s.image];
  const ogImage = `/images/${s.image}-${img.widths[img.widths.length - 1]}.jpg`;
  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${absoluteUrl(path)}#service`,
    name: s.h1,
    serviceType: s.name,
    description: s.seo.description,
    url: absoluteUrl(path),
    image: absoluteUrl(ogImage),
    provider: { '@id': `${SITE.origin}/#business` },
    areaServed: SITE.areaServed.map((name) => ({ '@type': 'City', name })),
  };

  return (
    <>
      <Seo
        title={s.seo.title}
        description={s.seo.description}
        keywords={s.seo.keywords}
        path={path}
        image={ogImage}
        jsonLd={[breadcrumbLd(trail), serviceLd, faqLd(s.detail.faqs)]}
      />

      {/* HERO */}
      <section className="relative w-full overflow-hidden bg-surface-ivory pt-space-lg pb-space-xl md:pb-20">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-gold-light/20 blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 -bottom-24 w-80 h-80 rounded-full bg-emerald-vivid/5 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <Breadcrumbs trail={trail} className="mb-space-lg" />
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-space-lg mb-space-xl">
            <div className="max-w-3xl flex flex-col items-start gap-space-md">
              <div className="inline-flex items-center gap-space-xs px-3 py-1 rounded bg-surface-cream text-secondary shadow-sm">
                <Icon name={s.discipline.icon} size={14} />
                <span className="font-label-sm text-label-sm uppercase tracking-widest">
                  Discipline {s.discipline.number} // {s.discipline.label}
                </span>
              </div>
              <h1 className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary tracking-tight animate-rise">
                {s.h1}
              </h1>
              <p className="font-headline-sm text-[20px] leading-8 md:text-headline-sm italic font-normal text-secondary animate-rise [animation-delay:60ms]">
                {s.headline}
              </p>
            </div>
            <div className="max-w-md flex flex-col gap-space-md lg:pb-space-xs">
              <p className="font-body-lg text-body-lg text-on-surface-variant animate-rise [animation-delay:80ms]">
                {s.detail.lead}
              </p>
              <div className="flex flex-wrap gap-space-sm">
                <AppLink href="/contact" className={btnPrimary}>
                  <Icon name="calendar_today" size={18} /> Book Free Consultation
                </AppLink>
                <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className={btnOutline}>
                  <Icon name="chat" size={18} /> WhatsApp
                </a>
              </div>
              <a
                href={`tel:${SITE.phoneE164}`}
                className="inline-flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary transition-colors"
              >
                <Icon name="call" size={16} /> Call {SITE.phoneDisplay}
              </a>
            </div>
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-md py-space-md px-space-md md:px-space-lg bg-surface-container-lowest/80 backdrop-blur-md rounded-xl shadow-sm">
            {s.facts.map((f) => (
              <div key={f.label} className="flex flex-col gap-1">
                <dt className="font-label-sm text-label-sm uppercase tracking-wider text-secondary">{f.label}</dt>
                <dd className="font-headline-sm text-[18px] leading-7 font-normal text-primary">{f.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="w-full py-space-xl md:py-20 bg-surface" id="overview">
        <div className={`${container} grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center`}>
          <div className="lg:col-span-6 flex flex-col gap-space-md">
            <span className={eyebrow}>Overview</span>
            <h2 className={h2Section}>Our Approach to {s.name}</h2>
            <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{s.intro}</p>
            {s.detail.overview.map((para) => (
              <p key={para.slice(0, 32)} className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                {para}
              </p>
            ))}
            <div className="space-y-2 pt-2">
              <h3 className="font-label-sm text-label-sm uppercase tracking-widest text-primary">
                Included Architectural Deliverables
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-body-sm text-body-sm text-on-surface-variant">
                {s.deliverables.map((d) => (
                  <li key={d} className="flex items-center gap-2">
                    <Icon name="check_circle" size={16} className="text-emerald-vivid" /> {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative w-full h-[340px] sm:h-[440px] lg:h-[560px] rounded-xl overflow-hidden shadow-xl bg-surface-container">
              <Picture
                name={s.image}
                alt={s.imageAlt}
                sizes="(min-width: 1024px) 50vw, 100vw"
                priority
                className="block w-full h-full"
                imgClassName="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/70 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded bg-surface-ivory/80 backdrop-blur-md shadow-md flex items-center justify-between gap-space-sm">
                <div>
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary block">
                    {s.featured.label}
                  </span>
                  {s.featured.project ? (
                    <AppLink
                      href={`/portfolio/${s.featured.project}`}
                      className="font-headline-sm text-headline-sm text-primary hover:text-secondary transition-colors"
                    >
                      {s.featured.title}
                    </AppLink>
                  ) : (
                    <span className="font-headline-sm text-headline-sm text-primary">{s.featured.title}</span>
                  )}
                </div>
                <Icon name={s.featured.icon} size={24} className="text-primary" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPACES */}
      <section className="w-full py-space-xl md:py-20 bg-surface-ivory">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-xl">
            <div>
              <span className={`${eyebrow} block mb-space-xs`}>Scope of Work</span>
              <h2 className={h2Section}>Spaces We Design</h2>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
              Every engagement is scoped to your brief — a single room or a complete turnkey project — and delivered by one
              accountable team.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-gutter">
            {s.detail.spaces.map((sp, i) => (
              <li
                key={sp.title}
                className="reveal flex flex-col gap-space-sm p-space-lg bg-surface rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <span className="font-headline-md text-headline-md text-gold-dark">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-headline-sm text-headline-sm text-primary">{sp.title}</h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{sp.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ProcessSection />

      {/* RELATED PROJECTS */}
      {related.length > 0 && (
        <section className="w-full py-space-xl md:py-20 bg-surface">
          <div className={container}>
            <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-lg">
              <div>
                <span className={`${eyebrow} block mb-space-xs`}>Curated Archive</span>
                <h2 className={h2Section}>Related Case Studies</h2>
              </div>
              <AppLink
                href="/portfolio"
                className="font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary flex items-center gap-1 transition-colors"
              >
                <span>View the full portfolio</span>
                <Icon name="arrow_forward" size={16} />
              </AppLink>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
              {related.map((p) => (
                <AppLink
                  key={p.slug}
                  href={`/portfolio/${p.slug}`}
                  className="reveal group relative block overflow-hidden rounded-lg bg-surface shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <div className="relative h-80 overflow-hidden">
                    <Picture
                      name={p.image}
                      alt={p.imageAlt}
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      className="block w-full h-full"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/90 via-botanical-dark/20 to-transparent" />
                    <div className="absolute top-4 left-4 px-3 py-1 rounded bg-surface-ivory/80 backdrop-blur font-label-sm text-label-sm uppercase tracking-wider text-primary">
                      {p.tag}
                    </div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="font-headline-sm text-headline-sm text-surface-ivory">{p.title}</h3>
                      <p className="font-body-sm text-body-sm text-gold-light/90 mt-1 line-clamp-2">{p.summary}</p>
                    </div>
                  </div>
                </AppLink>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="w-full py-space-xl md:py-20 bg-surface-ivory" id="faq">
        <div className={`${container} grid grid-cols-1 lg:grid-cols-12 gap-gutter`}>
          <div className="lg:col-span-4 flex flex-col gap-space-sm">
            <span className={eyebrow}>Questions, Answered</span>
            <h2 className={h2Section}>{s.name} FAQs</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Can&rsquo;t find what you are looking for? Our studio team is happy to talk through your project.
            </p>
            <AppLink
              href="/contact"
              className="inline-flex items-center gap-1 font-label-md text-label-md uppercase tracking-wider text-primary hover:text-secondary transition-colors"
            >
              Ask the studio <Icon name="arrow_forward" size={16} />
            </AppLink>
          </div>
          <div className="lg:col-span-8">
            <Faq items={s.detail.faqs} />
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="w-full py-space-xl md:py-20 bg-surface">
        <div className={container}>
          <div className="reveal mb-space-lg">
            <span className={`${eyebrow} block mb-space-xs`}>Pillars of Practice</span>
            <h2 className={h2Section}>Explore Our Other Disciplines</h2>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-gutter">
            {others.map((o) => (
              <li key={o.slug} className="reveal">
                <AppLink
                  href={`/services/${o.slug}`}
                  className="group h-full flex flex-col gap-space-sm p-space-lg bg-surface-ivory rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <span className="w-12 h-12 rounded bg-primary text-gold-light flex items-center justify-center">
                    <Icon name={o.icon} size={24} />
                  </span>
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">
                    Discipline {o.discipline.number} // {o.discipline.label}
                  </span>
                  <span className="font-headline-sm text-headline-sm text-primary group-hover:text-secondary transition-colors">
                    {o.name}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-1 font-label-md text-label-md uppercase tracking-wider text-primary">
                    Explore <Icon name="north_east" size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ConsultationBanner
        title={`Ready to Plan Your ${s.name} Project?`}
        text="Connect directly with our Principal Architect. We conduct comprehensive site audits and private concept reviews across Tamil Nadu, Bangalore, and Kerala."
      />
    </>
  );
}
